import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Minimal, polished sending-status pill for non-admin users.
 *
 * Behavior:
 *  - "Sending"            → green, when campaign is active and within window
 *  - "Outside hours"      → amber, when active but outside the sending window
 *  - "Paused"             → muted, when status=paused
 *  - "Inbox issue"        → red, when inbox health/last_error is set
 *  - "Draft - not started"→ muted
 *
 * No technical jargon, no debug buttons.
 */
export default function CampaignStatusPill({ campaign }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!campaign?.id || campaign.status !== "active") return;
    api
      .get(`/campaigns/${campaign.id}/diagnose`)
      .then((r) => setD(r.data))
      .catch(() => {});
  }, [campaign?.id, campaign?.status]);

  if (!campaign) return null;

  if (campaign.status === "draft") {
    return (
      <Pill tone="muted" icon={null}>
        Draft - activate to start sending
      </Pill>
    );
  }
  if (campaign.status === "paused") {
    return <Pill tone="muted">Paused</Pill>;
  }
  if (campaign.status === "completed") {
    return <Pill tone="muted">Completed</Pill>;
  }
  // active - check live state
  if (!d) {
    return <Pill tone="muted" icon={<Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.8} />}>Checking…</Pill>;
  }
  // Surface user-actionable issues only
  const inboxIssue = (d.inboxes || []).find((i) => i.last_error);
  if (inboxIssue) {
    return (
      <Pill tone="error" icon={<AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.8} />}>
        Inbox issue - check your connected mailbox
      </Pill>
    );
  }
  if (!d.within_window_now) {
    const next = d.next_window_open_utc
      ? new Date(d.next_window_open_utc).toLocaleString(undefined, {
          weekday: "short",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;
    return (
      <Pill tone="warn" icon={<AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.8} />}>
        Outside sending window{next ? ` · resumes ${next}` : ""}
      </Pill>
    );
  }
  if (d.cap_remaining <= 0) {
    return (
      <Pill tone="warn" icon={<AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.8} />}>
        Daily cap reached - resumes tomorrow
      </Pill>
    );
  }
  return (
    <Pill tone="ok" icon={<CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.8} />}>
      Sending live
    </Pill>
  );
}

function Pill({ tone = "muted", icon, children }) {
  const cls =
    tone === "ok"
      ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
      : tone === "warn"
      ? "border-amber-400/30 bg-amber-400/10 text-amber-200"
      : tone === "error"
      ? "border-red-400/30 bg-red-400/10 text-red-300"
      : "border-[#27272a] bg-[#0d0d0d] text-neutral-400";
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-mono ${cls}`}
      data-testid="campaign-status-pill"
    >
      {icon}
      {children}
    </div>
  );
}
